import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { FiX, FiGrid, FiList, FiInfo } from 'react-icons/fi';
import {
  MdWaterDrop, MdElectricalServices, MdEventSeat, MdAir, MdThermostat,
  MdTune, MdCleaningServices, MdPhoneAndroid, MdSettingsRemote,
  MdRotateLeft, MdSensors, MdClose, MdSettings, MdLightbulb, MdWaves,
  MdFemale, MdAutorenew, MdOutlineAir, MdShower, MdFlashOn, MdVerified,
  MdAccessibility, MdOutlineWaterDrop
} from 'react-icons/md';
import { useProducts } from '../contexts/ProductContext';
import { SALES_TAX_RATE } from '@bidetny/config';
import { Button } from './Button';
import { DisplayImage } from './DisplayImage';
import { Modal } from './Modal';
import { ImageViewerModal } from './ImageViewerModal';
import { GfciInformationModal } from './GfciInformationModal';
import { useOrder, useOrderRoute } from '../contexts/OrderContext';
import { PRODUCT_TYPE_LABELS } from '../types';

// Toggle Switch Component for Features View
interface ToggleSwitchProps {
  isExpanded: boolean;
  onToggle: (expanded: boolean) => void;
}

const ToggleSwitch: React.FC<ToggleSwitchProps> = ({ isExpanded, onToggle }) => {
  return (
    <div className="flex items-center bg-gray-100 rounded-full p-1">
      <button
        type="button"
        onClick={() => onToggle(false)}
        className={`flex items-center justify-center w-8 h-8 rounded-full transition-all duration-200 ${
          !isExpanded 
            ? 'bg-blue-500 text-white shadow-sm' 
            : 'text-gray-500 hover:text-gray-700'
        }`}
        title="Grid view"
      >
        <FiGrid className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => onToggle(true)}
        className={`flex items-center justify-center w-8 h-8 rounded-full transition-all duration-200 ${
          isExpanded 
            ? 'bg-blue-500 text-white shadow-sm' 
            : 'text-gray-500 hover:text-gray-700'
        }`}
        title="List view"
      >
        <FiList className="w-4 h-4" />
      </button>
    </div>
  );
};

// Features Section Component
interface FeaturesSectionProps {
  bidet: any;
  hideNotIncludedFeatures?: boolean;
  defaultFeatureDisplay: 'grid' | 'list';
}

const FeaturesSection: React.FC<FeaturesSectionProps> = ({ bidet, hideNotIncludedFeatures, defaultFeatureDisplay }) => {
  const [showFeaturesExpanded, setShowFeaturesExpanded] = useState(defaultFeatureDisplay === 'list');

  // Features configuration with icons and labels.
  // Catalog-rebuild-v2: keys must match FEATURE_FIELD_MAP labels in
  // app/data/productService.ts (which maps the v2 boolean columns onto these
  // display keys on the Product object). Power source, water heating,
  // primary control type, and product_type are enum columns surfaced
  // separately and not represented here.
  const featuresConfig = [
    // Comfort
    { key: 'Heated seat', label: 'Heated seat', icon: MdEventSeat },
    { key: 'Warm air dryer', label: 'Warm air dryer', icon: MdAir },
    { key: 'Adjustable water temperature', label: 'Adjustable water temperature', icon: MdThermostat },
    // Wash modes
    { key: 'Feminine wash', label: 'Feminine wash', icon: MdFemale },
    { key: 'Oscillation', label: 'Oscillation', icon: MdAutorenew },
    { key: 'Pulse / massage', label: 'Pulse / massage', icon: MdWaves },
    { key: 'Turbo / vortex wash', label: 'Turbo / vortex wash', icon: MdShower },
    { key: 'Pre-rinse', label: 'Pre-rinse', icon: MdOutlineWaterDrop },
    // Wash configuration
    { key: 'Self-cleaning nozzle', label: 'Self-cleaning nozzle', icon: MdCleaningServices },
    { key: 'Adjustable nozzle position', label: 'Adjustable nozzle position', icon: MdRotateLeft },
    { key: 'Adjustable spray pressure', label: 'Adjustable spray pressure', icon: MdTune },
    // Hygiene
    { key: 'Deodorizer', label: 'Deodorizer', icon: MdOutlineAir },
    { key: 'UV sterilization', label: 'UV sterilization', icon: MdFlashOn },
    // Controls
    { key: 'Companion mobile app', label: 'Companion mobile app', icon: MdPhoneAndroid },
    { key: 'Programmable presets', label: 'Programmable presets', icon: MdSettings },
    { key: 'Night light', label: 'Night light', icon: MdLightbulb },
    // Convenience
    { key: 'Soft-close seat', label: 'Soft-close seat', icon: MdEventSeat },
    { key: 'Auto-open seat', label: 'Auto-open seat', icon: MdSensors },
    { key: 'Auto-close seat', label: 'Auto-close seat', icon: MdClose },
    { key: 'Quick-release for cleaning', label: 'Quick-release', icon: MdCleaningServices },
    // Quality / accessibility
    { key: 'Lifetime warranty', label: 'Lifetime warranty', icon: MdVerified },
    { key: 'Commercial-grade', label: 'Commercial-grade', icon: MdSettingsRemote },
    { key: 'ADA-compliant', label: 'ADA-compliant', icon: MdAccessibility },
    { key: 'Left-handed friendly', label: 'Left-handed friendly', icon: MdAccessibility },
  ];

  // Get feature status for display. Three states:
  //  - 'included':     v === true              → blue
  //  - 'not_included': v === false             → orange
  //  - 'undetermined': v is null/undefined     → gray (we haven't verified)
  // Legacy 'yes'/'no' strings still get mapped to true/false respectively.
  type FeatureStatus = 'included' | 'not_included' | 'undetermined';
  const getFeatureStatus = (featureKey: string): FeatureStatus => {
    const v = (bidet as any)?.[featureKey];
    if (v === true || v === 'yes') return 'included';
    if (v === false || v === 'no') return 'not_included';
    return 'undetermined';
  };

  const includedFeatures = featuresConfig.filter(({ key }) => getFeatureStatus(key) === 'included');
  const notIncludedFeatures = featuresConfig.filter(({ key }) => getFeatureStatus(key) === 'not_included');
  const undeterminedFeatures = featuresConfig.filter(({ key }) => getFeatureStatus(key) === 'undetermined');

  // Render helper — keeps the grid/list code from being repeated three times.
  const renderFeatureGroup = (
    items: typeof featuresConfig,
    iconColorClass: string,
    listTextColorClass: string,
  ) => (
    showFeaturesExpanded ? (
      <div className="space-y-2">
        {items.map(({ key, label, icon: Icon }) => (
          <div key={key} className="flex items-center gap-2">
            <Icon className={`w-5 h-5 ${iconColorClass}`} />
            <span className={`text-sm ${listTextColorClass}`}>{label}</span>
          </div>
        ))}
      </div>
    ) : (
      <div className="grid grid-cols-6 sm:grid-cols-8 gap-2">
        {items.map(({ key, label, icon: Icon }) => (
          <div key={key} className="flex flex-col items-center" title={label}>
            <Icon className={`w-6 h-6 ${iconColorClass}`} />
          </div>
        ))}
      </div>
    )
  );

  return (
    <div className="mb-2">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-lg">Features:</h3>
        <ToggleSwitch
          isExpanded={showFeaturesExpanded}
          onToggle={setShowFeaturesExpanded}
        />
      </div>

      {/* Included Features */}
      <div className="mb-4">
        <h4 className="font-medium text-blue-600 mb-2">Included Features:</h4>
        {includedFeatures.length > 0
          ? renderFeatureGroup(includedFeatures, 'text-blue-600', 'text-gray-700')
          : <p className="text-sm text-gray-500 italic">No special features included</p>
        }
      </div>

      {/* Not Included Features */}
      { !hideNotIncludedFeatures && notIncludedFeatures.length > 0 && (
        <div className="mb-4">
          <h4 className="font-medium text-orange-500 mb-2">Not Included Features:</h4>
          {renderFeatureGroup(notIncludedFeatures, 'text-orange-500', 'text-gray-500')}
        </div>
      ) }

      {/* Undetermined Features — features we haven't verified for this product. */}
      { undeterminedFeatures.length > 0 && (
        <div>
          <h4 className="font-medium text-gray-500 mb-2">Undetermined Features:</h4>
          <p className="text-xs text-gray-400 mb-2 italic">
            We haven't verified these features for this product. Check the brand site for confirmation.
          </p>
          {renderFeatureGroup(undeterminedFeatures, 'text-gray-400', 'text-gray-500')}
        </div>
      ) }
    </div>
  );
};

// BidetDetails component that can be reused in modal or landing pages
interface BidetDetailsProps {
  bidetId: string;
  showCloseButton?: boolean;
  onClose?: () => void;
  className?: string;
  hideNotIncludedFeatures?: boolean;
  hideProductDetails?: boolean;
  showPriceWithTax?: boolean;
  defaultFeatureDisplay?: 'grid' | 'list';
  ctaLabel?: string;
  onCtaClick?: (productId: string, variationId: string) => void;
}

export const BidetDetails: React.FC<BidetDetailsProps> = ({
  bidetId,
  showCloseButton = false,
  onClose,
  className = "",
  hideNotIncludedFeatures = false,
  hideProductDetails = false,
  showPriceWithTax = false,
  defaultFeatureDisplay = 'grid',
  ctaLabel,
  onCtaClick,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const orderRoute = useOrderRoute();
  const { updateOrder } = useOrder();
  const { products: bidets } = useProducts();

  const [showFullOverview, setShowFullOverview] = useState(false);
  const [showFullWhy, setShowFullWhy] = useState(false);
  const [selectedVariationId, setSelectedVariationId] = useState<string | null>(null);
  const [showGfciModal, setShowGfciModal] = useState(false);
  const [imageViewerOpenIndex, setImageViewerOpenIndex] = useState<number | null>(null);

  const bidet = bidets.find((b) => b.id === bidetId);

  // Find the default variation or first variation
  const defaultVariation = bidet?.variations?.find(v => v.isDefaultVariation) || bidet?.variations?.[0];
  const selectedVariation = selectedVariationId 
    ? bidet?.variations?.find(v => v.id === selectedVariationId)
    : defaultVariation;

  // Set initial selected variation when bidet changes
  useEffect(() => {
    if (bidet && defaultVariation) {
      setSelectedVariationId(defaultVariation.id);
    }
  }, [bidet?.id]);

  // Helper to get correct fields from product and variation
  function getTitle(bidet: any) {
    return bidet?.Title || bidet?.['Model Name'] || bidet?.Model || '';
  }
  function getPrice(variation: any) {
    // Catalog-rebuild-v2: many products don't have reseller_prices rows
    // populated yet. Fall back to MSRP so the storefront still shows a
    // price (matches the data we DO have for every variation).
    return variation?.resellerPrice || variation?.msrp || '';
  }
  function getSubtitle(bidet: any) {
    return bidet?.Subtitle || '';
  }
  function getWhyWeLoveIt(bidet: any) {
    return bidet?.['Why We Love It'] || '';
  }
  function getOverview(bidet: any) {
    return bidet?.Overview || '';
  }
  function getResellerUrl(variation: any) {
    return variation?.resellerUrl || '';
  }
  function getBrandUrl(variation: any, productLevelFallback?: string) {
    // Per-variation brand URL is preferred (e.g. Moen sells biscuit and white
    // as distinct Shopify pages), but most products don't have a per-variation
    // page — for those, fall back to the product-level manufacturer URL.
    return variation?.brandUrl || productLevelFallback || '';
  }
  function isRecommended(bidet: any) {
    return typeof bidet?.['Recommended Category'] === 'string' && bidet['Recommended Category'].length > 0;
  }

  // Combine product images with variation-specific images
  function getCombinedImages(bidet: any, variation: any) {
    const productImages = bidet?.images || [];
    const variationImages = variation?.images || [];
    return [...productImages, ...variationImages];
  }
  
  // Helper to split and count words
  const getWords = (text: string) => text.trim().split(/\s+/);
  const whyWords = getWhyWeLoveIt(bidet) ? getWords(getWhyWeLoveIt(bidet)) : [];
  const overviewWords = getOverview(bidet) ? getWords(getOverview(bidet)) : [];
  const WHY_LIMIT = 80;

  // --- Find install price for this bidet ---
  let installPrice: number | null = null;
  if (bidet && bidet['Installation Type']) {
    // Catalog-rebuild-v2: 'bidet installation' → 'installation_service'.
    const installProduct = bidets.find(
      (p) => p.Category === 'installation_service' && p['Product ID'] === bidet['Installation Type']
    );
    if (installProduct && installProduct.variations && installProduct.variations.length > 0) {
      installPrice = installProduct.variations[0].msrp || null;
    } else {
      installPrice = null;
    }
  }
  // --- End install price logic ---

  if (!bidet || !selectedVariation) return null;

  const combinedImages = getCombinedImages(bidet, selectedVariation);

  return (
    <>
      <div className={className}>
        {/* Title and close button */}
        {showCloseButton && (
          <nav className="sticky top-0 z-10 bg-white bg-opacity-95 flex items-center justify-between px-8 py-4 border-b border-gray-200 rounded-t-lg">
            <div className="flex flex-col flex-1 min-w-0 pr-4">
              <div className="flex items-center gap-2 min-w-0 pr-4">
                <h2 className="text-2xl font-bold mb-0 break-words whitespace-normal">{getTitle(bidet)}</h2>
                {/* Hide Recommended tag on mobile, show on desktop */}
                {isRecommended(bidet) && (
                  <span className="hidden sm:inline-block bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-semibold ml-2">
                    Recommended
                  </span>
                )}
              </div>
              {getSubtitle(bidet) && (
                <div className="text-sm text-gray-500 mt-1 break-words whitespace-normal">{getSubtitle(bidet)}</div>
              )}
            </div>
            <Button
              type="button"
              variant="text"
              className="self-start mt-0 text-gray-700 text-3xl font-bold bg-white bg-opacity-80 rounded-full w-10 h-10 flex items-center justify-center"
              aria-label="Close modal"
              onClick={onClose}
            >
              <FiX className="w-8 h-8" />
            </Button>
          </nav>
        )}
        
        {/* Catalog-accuracy disclaimer */}
        <div className="px-8 pt-4">
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-900 text-xs sm:text-sm rounded-md px-3 py-2">
            <FiInfo className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>
              Our full bidet catalog is still being developed and may contain
              inaccuracies. Please refer to the official brand websites for
              official product claims.
            </span>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-8 p-8 pt-4">
          {/* Left/Main column */}
          <div className="w-full lg:w-1/2 min-w-0">
            {/* Title for non-modal usage */}
            {!showCloseButton && (
              <div className="mb-4">
                <div className="flex items-center gap-2 min-w-0 pr-4">
                  <h2 className="text-2xl font-bold mb-0 break-words whitespace-normal">{getTitle(bidet)}</h2>
                  {/* Hide Recommended tag on mobile, show on desktop */}
                  {isRecommended(bidet) && (
                    <span className="hidden sm:inline-block bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-semibold ml-2">
                      Recommended
                    </span>
                  )}
                </div>
                {getSubtitle(bidet) && (
                  <div className="text-sm text-gray-500 mt-1 break-words whitespace-normal">{getSubtitle(bidet)}</div>
                )}
              </div>
            )}

            <div>
              {combinedImages.length > 0 && (
                <DisplayImage
                  images={combinedImages}
                  imgClassName="mb-2 w-full sm:h-64"
                  onMainImageClick={(idx) => setImageViewerOpenIndex(idx)}
                />
              )}
            </div>

            {/* Variation Selection */}
            {bidet.variations && bidet.variations.length > 1 && (
              <div className="mb-4">
                <h4 className="font-semibold mb-2 mt-2">Select Options:</h4>
                <div className="space-y-2">
                  {/* Color selection */}
                  {[...new Set(bidet.variations.map(v => v.color))].filter(Boolean).length > 1 && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">Color:</span>
                      <div className="flex flex-wrap gap-1">
                        {[...new Set(bidet.variations.map(v => v.color))].filter(Boolean).map(color => {
                          const isColorSelected = selectedVariation.color === color;
                          return (
                            <button
                              key={color}
                              type="button"
                              onClick={() => {
                                const newVariation = bidet.variations.find(v => 
                                  v.color === color && v.style === selectedVariation.style
                                );
                                if (newVariation) setSelectedVariationId(newVariation.id);
                              }}
                              className={`px-3 py-1 text-sm border rounded-full transition-colors ${
                                isColorSelected 
                                  ? 'bg-blue-600 text-white border-blue-600' 
                                  : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:bg-blue-50'
                              }`}
                            >
                              {color}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* Style selection */}
                  {[...new Set(bidet.variations.map(v => v.style))].filter(Boolean).length > 1 && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">Style:</span>
                      <div className="flex flex-wrap gap-1">
                        {[...new Set(bidet.variations.map(v => v.style))].filter(Boolean).map(style => {
                          const isStyleSelected = selectedVariation.style === style;
                          return (
                            <button
                              key={style}
                              type="button"
                              onClick={() => {
                                const newVariation = bidet.variations.find(v => 
                                  v.style === style && v.color === selectedVariation.color
                                );
                                if (newVariation) setSelectedVariationId(newVariation.id);
                              }}
                              className={`px-3 py-1 text-sm border rounded-full transition-colors ${
                                isStyleSelected 
                                  ? 'bg-blue-600 text-white border-blue-600' 
                                  : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:bg-blue-50'
                              }`}
                            >
                              {style}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Price row and install button layout */}
            <div className="flex flex-col gap-2 mb-2 mt-4">
              <div className="flex flex-row gap-4">
                {/* Bidet Price */}
                <div className="flex flex-col">
                  <span className="text-xs text-gray-500">Bidet Price*</span>
                  <span className="text-xl text-blue-600 m-0 font-bold">
                    {selectedVariation && getPrice(selectedVariation) ? `$${getPrice(selectedVariation)}` : '—'}
                  </span>
                </div>
                {/* Install Price */}
                <div className="flex flex-col">
                  <span className="text-xs text-gray-500">Install Price*</span>
                  <span className="text-xl text-blue-600 m-0 font-bold">
                    {installPrice !== null ? `$${installPrice.toFixed(2)}` : '—'}
                  </span>
                </div>
                {/* Total Price */}
                <div className="flex flex-col">
                  <span className="text-xs text-gray-500">Total Price*</span>
                  <span className="text-xl text-green-700 m-0 font-bold">
                    {(selectedVariation && getPrice(selectedVariation) && installPrice !== null)
                      ? `$${(parseFloat(getPrice(selectedVariation)) + installPrice).toFixed(2)}`
                      : '—'}
                  </span>
                </div>
                {/* Total Price With Tax */}
                {showPriceWithTax && (
                  <div className="flex flex-col">
                    <span className="text-xs text-gray-500">Total With Tax</span>
                    <span className="text-xl text-purple-600 m-0 font-bold">
                      {(selectedVariation && getPrice(selectedVariation) && installPrice !== null)
                        ? `$${((parseFloat(getPrice(selectedVariation)) + installPrice) * (1 + SALES_TAX_RATE)).toFixed(2)}`
                        : '—'}
                    </span>
                  </div>
                )}
              </div>
              {/* Button: below prices on mobile, next to prices on desktop */}
              <div className="w-full sm:w-auto">
                {(() => {
                  // Catalog-rebuild-v2: per-variation install eligibility.
                  // 'installable'  → normal CTA, opens the order flow.
                  // 'listed_only'  → we list it but don't install; show a
                  //                  contact-us hint instead of a CTA.
                  // 'discontinued' → manufacturer no longer makes it; same
                  //                  as listed_only but with a different copy.
                  const status = (selectedVariation as any)?.bnyInstallStatus || 'installable';
                  if (status !== 'installable') {
                    const note = (selectedVariation as any)?.bnyNotInstallNote;
                    const heading = status === 'discontinued'
                      ? "We don't install this — it's been discontinued."
                      : "We don't install this one.";
                    return (
                      <div className="w-full sm:w-auto bg-gray-100 border border-gray-300 rounded-lg px-4 py-2 text-sm text-gray-700 mt-2 sm:mt-0" style={{ minWidth: '140px', maxWidth: '420px' }}>
                        <div className="font-semibold mb-1">{heading}</div>
                        {note && <div className="text-xs text-gray-600">{note}</div>}
                        <div className="text-xs text-gray-600 mt-1">
                          Reach out if you'd like a referral or have questions.
                        </div>
                      </div>
                    );
                  }
                  return (
                    <Button
                      type="button"
                      cta
                      className="w-full sm:w-auto bg-yellow-400 hover:bg-yellow-300 text-black font-bold py-1.5 px-3 rounded-full shadow transition-colors duration-150 text-sm border-2 border-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500 mt-2 sm:mt-0"
                      style={{ minWidth: '140px' }}
                      onClick={() => {
                        if (selectedVariation) {
                          if (onCtaClick) {
                            onCtaClick(bidet.id, selectedVariation.id);
                          } else {
                            updateOrder({
                              items: [{ productId: bidet.id, variationId: selectedVariation.id, model: getTitle(bidet) }],
                              quantity: 1,
                            });
                            if (onClose) onClose();
                            // Route to /ordering-paused if the admin kill-switch is OFF;
                            // otherwise to /order. Skip if we're already there.
                            if (!location.pathname.startsWith('/order') && location.pathname !== '/ordering-paused') {
                              navigate(orderRoute);
                            }
                          }
                        }
                      }}
                    >
                      {ctaLabel || 'Get this bidet installed'}
                    </Button>
                  );
                })()}
              </div>
            </div>
            <div className="text-xs text-gray-500 mb-2">* All prices shown are pre-tax</div>

            { !hideProductDetails && (
              <>
                {/* Show Recommended tag below price on mobile only */}
                {isRecommended(bidet) && (
                  <div className="sm:hidden mb-2">
                    <span className="inline-block bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-semibold">
                      Recommended
                    </span>
                  </div>
                )}
                <div className="mb-2">
                  <span className="font-semibold">Manufacturer:</span>
                  {' '}
                  {bidet.manufacturer}
                </div>
                <div className="mb-2">
                  <span className="font-semibold">Model:</span>
                  {' '}
                  {bidet.Model || bidet['Model Name'] || ''}
                </div>
                <div className="mb-2">
                  <span className="font-semibold">Category:</span>
                  {' '}
                  {(PRODUCT_TYPE_LABELS as any)[bidet.Category] || bidet.Category}
                </div>
                {selectedVariation.color && (
                  <div className="mb-2">
                    <span className="font-semibold">Color:</span>
                    {' '}
                    {selectedVariation.color}
                  </div>
                )}
                {selectedVariation.style && (
                  <div className="mb-2">
                    <span className="font-semibold">Style:</span>
                    {' '}
                    {selectedVariation.style}
                  </div>
                )}
                {(getResellerUrl(selectedVariation) || getBrandUrl(selectedVariation, bidet.manufacturerUrl)) && (
                  <div className="mb-2 space-x-4">
                    {getResellerUrl(selectedVariation) && (
                      <a
                        href={getResellerUrl(selectedVariation)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 underline"
                      >
                        View on Reseller Site
                      </a>
                    )}
                    {getBrandUrl(selectedVariation, bidet.manufacturerUrl) && (
                      <a
                        href={getBrandUrl(selectedVariation, bidet.manufacturerUrl)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 underline"
                      >
                        View on Brand Site
                      </a>
                    )}
                  </div>
                )}
              </>
            ) }
          </div>
          {/* Right/Side column */}
          <div className="w-full lg:w-1/2 space-y-6">
            
            {getOverview(bidet) && (
              <div className="mb-2">
                <h3 className="font-semibold text-lg mb-1">Overview:</h3>
                <p className="text-sm text-gray-700">
                  {overviewWords.length > WHY_LIMIT && !showFullOverview
                    ? `${overviewWords.slice(0, WHY_LIMIT).join(' ')}...`
                    : getOverview(bidet)}
                </p>
                {overviewWords.length > WHY_LIMIT && (
                  <Button
                    type="button"
                    variant="text"
                    className="text-blue-500 underline text-xs mt-1 focus:outline-none"
                    onClick={() => setShowFullOverview((v) => !v)}
                  >
                    {showFullOverview ? 'View less' : 'View more'}
                  </Button>
                )}
              </div>
            )}
            {getWhyWeLoveIt(bidet) && (
              <div className="mb-2">
                <h3 className="font-semibold text-lg mb-1">Why we love it:</h3>
                <p className="text-sm text-gray-700">
                  {whyWords.length > WHY_LIMIT && !showFullWhy
                    ? `${whyWords.slice(0, WHY_LIMIT).join(' ')}...`
                    : getWhyWeLoveIt(bidet)}
                </p>
                {whyWords.length > WHY_LIMIT && (
                  <Button
                    type="button"
                    variant="text"
                    className="text-blue-500 underline text-xs mt-1 focus:outline-none"
                    onClick={() => setShowFullWhy((v) => !v)}
                  >
                    {showFullWhy ? 'View less' : 'View more'}
                  </Button>
                )}
              </div>
            )}
            {(bidet.power_source === 'electric' || bidet.power_source === 'electric_with_battery_backup') && (
              <div className="mb-2">
                <h3 className="font-semibold text-yellow-700 text-lg mb-1">Caution:</h3>
                <p className="text-sm text-yellow-700">
                  This bidet requires electricity to function. Please ensure you have a nearby outlet for installation. For your safety, manufacturers highly recommend a <Button
                    variant="text"
                    className="text-yellow-700 underline"
                    onClick={() => setShowGfciModal(true)}
                  >
                    GFCI outlet
                  </Button> (with a "test" and a "reset" button).
                </p>
              </div>
            )}
            
            <FeaturesSection
              bidet={bidet}
              hideNotIncludedFeatures={hideNotIncludedFeatures}
              defaultFeatureDisplay={defaultFeatureDisplay}
            />

          </div>
        </div>
      </div>
      <GfciInformationModal
        open={showGfciModal}
        onClose={() => setShowGfciModal(false)}
      />
      <ImageViewerModal
        open={imageViewerOpenIndex !== null}
        images={combinedImages.map((img) => ({ localPath: img.localPath, alt: getTitle(bidet) }))}
        initialIndex={imageViewerOpenIndex ?? 0}
        onClose={() => setImageViewerOpenIndex(null)}
      />
    </>
  );
};

export const BidetModal: React.FC = () => {
  const { selectedBidetIdForBidetModal, closeBidetModal } = useOrder();

  if (!selectedBidetIdForBidetModal) return null;

  return (
    <Modal open onClose={closeBidetModal}>
      <div onClick={(e) => e.stopPropagation()}>
        <BidetDetails 
          bidetId={selectedBidetIdForBidetModal}
          showCloseButton={true}
          onClose={closeBidetModal}
        />
      </div>
    </Modal>
  );
};
